import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const StockTransferModal = ({ isOpen, onClose, selectedItems, onTransfer }) => {
  const [transferData, setTransferData] = useState({
    fromLocation: '',
    toLocation: '',
    quantity: '',
    reason: '',
    notes: ''
  });

  const locationOptions = [
    { value: 'warehouse-a', label: 'Warehouse A' },
    { value: 'warehouse-b', label: 'Warehouse B' },
    { value: 'production-floor', label: 'Production Floor' },
    { value: 'quality-check', label: 'Quality Check Area' },
    { value: 'dispatch', label: 'Dispatch Section' },
    { value: 'return-section', label: 'Return Section' }
  ];

  const reasonOptions = [
    { value: 'production-requirement', label: 'Production Requirement' },
    { value: 'quality-check', label: 'Quality Check' },
    { value: 'dispatch-preparation', label: 'Dispatch Preparation' },
    { value: 'return-processing', label: 'Return Processing' },
    { value: 'inventory-optimization', label: 'Inventory Optimization' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'other', label: 'Other' }
  ];

  const handleTransfer = () => {
    onTransfer({
      items: selectedItems,
      transferData
    });
    onClose();
    setTransferData({
      fromLocation: '',
      toLocation: '',
      quantity: '',
      reason: '',
      notes: ''
    });
  };

  const handleInputChange = (field, value) => {
    setTransferData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1001">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="ArrowRightLeft" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Stock Transfer</h2>
              <p className="text-sm text-muted-foreground">
                Transfer {selectedItems.length} item(s) between locations
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        <div className="p-6 space-y-6">
          {/* From Location */}
          <div>
            <Select
              label="From Location"
              description="Current location of the items"
              options={locationOptions}
              value={transferData.fromLocation}
              onChange={(value) => handleInputChange('fromLocation', value)}
              required
            />
          </div>

          {/* To Location */}
          <div>
            <Select
              label="To Location"
              description="Destination location for the items"
              options={locationOptions.filter(loc => loc.value !== transferData.fromLocation)}
              value={transferData.toLocation}
              onChange={(value) => handleInputChange('toLocation', value)}
              required
            />
          </div>

          {/* Quantity */}
          <div>
            <Input
              label="Quantity to Transfer"
              type="number"
              min="1"
              description="Number of pieces to transfer"
              value={transferData.quantity}
              onChange={(e) => handleInputChange('quantity', e.target.value)}
              required
            />
          </div>

          {/* Reason */}
          <div>
            <Select
              label="Transfer Reason"
              description="Select the reason for this stock transfer"
              options={reasonOptions}
              value={transferData.reason}
              onChange={(value) => handleInputChange('reason', value)}
              required
            />
          </div>

          {/* Notes */}
          <div>
            <Input
              label="Additional Notes"
              type="text"
              description="Optional notes about this transfer"
              value={transferData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Enter any additional information..."
            />
          </div>

          {/* Transfer Summary */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="font-medium text-foreground mb-3">Transfer Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Items Selected:</span>
                <span className="text-foreground font-medium">{selectedItems.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">From:</span>
                <span className="text-foreground font-medium">
                  {transferData.fromLocation ? 
                    locationOptions.find(loc => loc.value === transferData.fromLocation)?.label : 
                    'Not selected'
                  }
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">To:</span>
                <span className="text-foreground font-medium">
                  {transferData.toLocation ? 
                    locationOptions.find(loc => loc.value === transferData.toLocation)?.label : 
                    'Not selected'
                  }
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Quantity:</span>
                <span className="text-foreground font-medium">
                  {transferData.quantity || 'Not specified'} pieces
                </span>
              </div>
            </div>
          </div>

          {/* Selected Items Preview */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="font-medium text-foreground mb-3">Selected Items</h3>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {selectedItems.slice(0, 5).map((item, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-foreground">Floral Print - Size M - Piece #{item}</span>
                  <span className="text-muted-foreground">Available: 25 pcs</span>
                </div>
              ))}
              {selectedItems.length > 5 && (
                <div className="text-sm text-muted-foreground">
                  +{selectedItems.length - 5} more items...
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/20">
          <div className="text-sm text-muted-foreground">
            Ensure all details are correct before proceeding
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleTransfer}
              disabled={!transferData.fromLocation || !transferData.toLocation || !transferData.quantity}
              iconName="ArrowRightLeft"
              iconPosition="left"
              iconSize={16}
            >
              Transfer Stock
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockTransferModal;